// Lightweight toast surface — driven by the WheelPlacementController's
// onToast callback. Mirrors iOS's `showToast: (String) -> Void` pattern;
// auto-dismisses after a few seconds.

import { useEffect, type CSSProperties } from 'react'

interface ToastProps {
  message: string | null
  durationMs?: number
  onDismiss: () => void
  /** Merged last — ?use_ios_ui restyles the surface, nothing else. */
  style?: CSSProperties
}

export function Toast({ message, durationMs = 3000, onDismiss, style }: ToastProps) {
  useEffect(() => {
    if (message == null) return undefined
    const timer = setTimeout(onDismiss, durationMs)
    return () => clearTimeout(timer)
  }, [message, durationMs, onDismiss])

  if (message == null) return null
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="toast"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 110,
        transform: 'translateX(-50%)',
        background: 'rgba(0, 0, 0, 0.85)',
        color: 'white',
        padding: '10px 18px',
        borderRadius: 22,
        fontSize: 14,
        fontWeight: 500,
        pointerEvents: 'none',
        maxWidth: 'min(80vw, 360px)',
        textAlign: 'center',
        ...style,
      }}
    >
      {message}
    </div>
  )
}
